import BlockIcon from '@mui/icons-material/Block';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DirectionsCarRoundedIcon from '@mui/icons-material/DirectionsCarRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type React from 'react';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  STATUS_GOOD,
  STATUS_WARN,
  SURFACE_CONTAINER_LOWEST,
  SURFACE_HOVER,
  YELLOW,
} from 'styles/tokens';

import type { CampaignUploadResult } from './CampaignConfiguration';

interface Props {
  result: CampaignUploadResult;
  onClose: () => void;
}

type Tone = 'good' | 'warn' | 'error' | 'neutral';

const toneColor: Record<Tone, string> = {
  good: STATUS_GOOD,
  warn: STATUS_WARN,
  error: '#B00020',
  neutral: NAVY,
};

const toneSurface: Record<Tone, string> = {
  good: 'rgba(83, 183, 33, 0.08)',
  warn: 'rgba(207, 92, 9, 0.08)',
  error: 'rgba(176, 0, 32, 0.07)',
  neutral: SURFACE_HOVER,
};

const Row: React.FC<{
  icon: React.ReactNode;
  count: number;
  label: string;
  detail?: string;
  tone: Tone;
}> = ({ icon, count, label, detail, tone }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      px: 2,
      py: 1.5,
      bgcolor: toneSurface[tone],
      borderRadius: RADIUS_ITEM,
    }}
  >
    <Box
      sx={{
        flexShrink: 0,
        width: 36,
        height: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: SURFACE_CONTAINER_LOWEST,
        borderRadius: RADIUS_ITEM,
        color: toneColor[tone],
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography
          sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, fontSize: '1.25rem', color: toneColor[tone] }}
        >
          {count}
        </Typography>
        <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 500, fontSize: '0.95rem', color: NAVY }}>
          {label}
        </Typography>
      </Box>
      {detail && (
        <Typography
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontSize: '0.8rem',
            color: NAVY,
            opacity: 0.6,
            mt: 0.25,
            wordBreak: 'break-word',
          }}
        >
          {detail}
        </Typography>
      )}
    </Box>
  </Box>
);

const CampaignUploadSummaryDialog: React.FC<Props> = ({ result, onClose }) => {
  const duplicatePhones = result.skippedClients.filter((c) => c.reason === 'Teléfono duplicado');
  const missingPhones = result.skippedClients.filter((c) => c.reason === 'Sin teléfono');
  const otherSkipped = result.skippedClients.filter(
    (c) => c.reason !== 'Teléfono duplicado' && c.reason !== 'Sin teléfono'
  );

  const duplicateSample = duplicatePhones
    .slice(0, 4)
    .map((c) => `fila ${c.row}: ${c.phone}`)
    .join(', ');
  const duplicateExtra = duplicatePhones.length > 4 ? ` (+${duplicatePhones.length - 4} más)` : '';

  const failedSample = result.failedClients
    .slice(0, 2)
    .map((c) => `${c.phone}: ${c.error}`)
    .join(' · ');
  const failedExtra = result.failedClients.length > 2 ? ` (+${result.failedClients.length - 2} más)` : '';

  const skippedCars = result.skippedCars ?? [];
  const skippedCarsSample = skippedCars
    .slice(0, 4)
    .map((c) => `fila ${c.row}: ${c.identifier}`)
    .join(', ');
  const skippedCarsExtra = skippedCars.length > 4 ? ` (+${skippedCars.length - 4} más)` : '';

  const failedCars = result.failedCars ?? [];
  const failedCarsSample = failedCars
    .slice(0, 2)
    .map((c) => `${c.identifier}: ${c.error}`)
    .join(' · ');
  const failedCarsExtra = failedCars.length > 2 ? ` (+${failedCars.length - 2} más)` : '';

  const parseErrors = result.parseErrors ?? [];
  const parseSample = parseErrors
    .slice(0, 3)
    .map((p) => (p.row ? `fila ${p.row}: ${p.message}` : p.message))
    .join(' · ');
  const parseExtra = parseErrors.length > 3 ? ` (+${parseErrors.length - 3} más)` : '';

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            bgcolor: GLASS_BACKGROUND,
            backdropFilter: GLASS_BLUR,
            WebkitBackdropFilter: GLASS_BLUR,
            borderRadius: RADIUS_CARD,
            boxShadow: SHADOW,
            p: 1,
          },
        },
        backdrop: {
          sx: { bgcolor: 'rgba(21, 24, 58, 0.18)', backdropFilter: 'blur(2px)' },
        },
      }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: RADIUS_ITEM,
              bgcolor: 'rgba(83, 183, 33, 0.12)',
              color: STATUS_GOOD,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircleRoundedIcon />
          </Box>
          <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, fontSize: '1.25rem', color: NAVY }}>
            Campaña creada
          </Typography>
        </Box>
        <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.9rem', color: NAVY, opacity: 0.6, ml: 6.5 }}>
          Resumen de procesamiento del archivo
        </Typography>
      </Box>

      <Box sx={{ px: 3, pb: 1 }}>
        <Stack spacing={1}>
          <Row
            tone="good"
            icon={<PersonAddAlt1RoundedIcon fontSize="small" />}
            count={result.createdClients.length}
            label="clientes nuevos creados"
          />
          <Row
            tone="neutral"
            icon={<RefreshRoundedIcon fontSize="small" />}
            count={result.updatedClients.length}
            label="clientes existentes actualizados"
          />
          {result.createdCars > 0 && (
            <Row
              tone="good"
              icon={<DirectionsCarRoundedIcon fontSize="small" />}
              count={result.createdCars}
              label="vehículos creados"
            />
          )}
          {result.updatedCars > 0 && (
            <Row
              tone="neutral"
              icon={<DirectionsCarRoundedIcon fontSize="small" />}
              count={result.updatedCars}
              label="vehículos existentes actualizados"
            />
          )}
          {skippedCars.length > 0 && (
            <Row
              tone="warn"
              icon={<WarningAmberRoundedIcon fontSize="small" />}
              count={skippedCars.length}
              label="vehículos omitidos — falta marca o modelo"
              detail={`${skippedCarsSample}${skippedCarsExtra}`}
            />
          )}
          {failedCars.length > 0 && (
            <Row
              tone="error"
              icon={<ErrorOutlineRoundedIcon fontSize="small" />}
              count={failedCars.length}
              label="vehículos fallidos"
              detail={`${failedCarsSample}${failedCarsExtra}`}
            />
          )}
          {duplicatePhones.length > 0 && (
            <Row
              tone="warn"
              icon={<WarningAmberRoundedIcon fontSize="small" />}
              count={duplicatePhones.length}
              label="filas omitidas — teléfono duplicado"
              detail={`${duplicateSample}${duplicateExtra}`}
            />
          )}
          {missingPhones.length > 0 && (
            <Row
              tone="warn"
              icon={<WarningAmberRoundedIcon fontSize="small" />}
              count={missingPhones.length}
              label="filas omitidas — sin teléfono"
            />
          )}
          {otherSkipped.length > 0 && (
            <Row
              tone="warn"
              icon={<WarningAmberRoundedIcon fontSize="small" />}
              count={otherSkipped.length}
              label="filas omitidas por otros motivos"
            />
          )}
          {parseErrors.length > 0 && (
            <Row
              tone="warn"
              icon={<WarningAmberRoundedIcon fontSize="small" />}
              count={parseErrors.length}
              label="filas con problemas de formato CSV"
              detail={`${parseSample}${parseExtra}`}
            />
          )}
          {result.failedClients.length > 0 && (
            <Row
              tone="error"
              icon={<ErrorOutlineRoundedIcon fontSize="small" />}
              count={result.failedClients.length}
              label="clientes fallidos"
              detail={`${failedSample}${failedExtra}`}
            />
          )}
          {result.aborted && (
            <Row
              tone="error"
              icon={<BlockIcon fontSize="small" />}
              count={0}
              label="Procesamiento abortado por exceder umbral de errores"
            />
          )}
        </Stack>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 3, pt: 2, pb: 2.5 }}>
        <Button
          onClick={onClose}
          sx={{
            bgcolor: YELLOW,
            color: NAVY,
            fontFamily: 'Jost, sans-serif',
            fontWeight: 700,
            borderRadius: RADIUS_COIN,
            px: 3.5,
            py: 0.85,
            boxShadow: SHADOW,
            textTransform: 'none',
            '&:hover': { bgcolor: '#e6c009' },
          }}
        >
          Listo
        </Button>
      </Box>
    </Dialog>
  );
};

export default CampaignUploadSummaryDialog;
